import { useState, useEffect } from "react";
import { Menu, X, ArrowLeft, ArrowRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../ui/button";
import LanguageSwitcher from "../LanguageSwitcher";
import { useLanguage } from "../../contexts/LanguageContext";

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showTrustBar, setShowTrustBar] = useState(true);
  const { language, direction, translations: t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = (e, href) => {
    if (href.startsWith('#')) {
      e.preventDefault();
      const targetId = href.substring(1);
      const target = document.getElementById(targetId);
      if (target) {
        const headerOffset = showTrustBar ? 120 : 80;
        const elementPosition = target.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
        window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
      }
    }
  };

  const navLinks = [
    { href: "#about", label: t.nav.about },
    { href: "#treatments", label: t.nav.treatments },
    { href: "#results", label: t.nav.results },
    { href: "#testimonials", label: t.nav.testimonials },
    { href: "#faq", label: language === 'he' ? "שאלות נפוצות" : "FAQ" },
    { href: "#contact", label: t.nav.contact },
    { href: "/blog", label: language === 'he' ? "מאמרים" : "Blog" },
    { href: "/laser", label: language === 'he' ? "✨ לייזר / IPL" : "✨ Laser / IPL", isSpecial: true },
  ];

  const ArrowIcon = direction === 'rtl' ? ArrowLeft : ArrowRight;

  return (
    <>
      <a href="#main-content" className="skip-link">
        {language === 'he' ? 'דלג לתוכן הראשי' : 'Skip to main content'}
      </a>
      
      {showTrustBar && (
        <div 
          className="fixed top-0 left-0 right-0 z-[60] bg-[#1A1008] text-[#D4B483] text-center py-2.5 px-5 text-xs tracking-wider"
          data-testid="trust-bar"
        >
          <div className="flex items-center justify-center gap-2 md:gap-4 flex-wrap">
            <span>{language === 'he' ? '40 שנות ניסיון פרא-רפואי' : '40 years of paramedical experience'}</span>
            <span className="hidden md:inline">|</span>
            <span>{language === 'he' ? '3,000+ לקוחות מרוצים' : '3,000+ satisfied clients'}</span>
            <span className="hidden md:inline">|</span>
            <span>{language === 'he' ? 'ייעוץ ראשון ללא עלות' : 'First consultation free'}</span>
          </div>
          <button 
            onClick={() => setShowTrustBar(false)}
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-transparent border-none text-[#D4B483] cursor-pointer text-base opacity-50 hover:opacity-100 p-1"
            aria-label="Close"
          >
            ✕
          </button>
        </div>
      )}
      
      <header
        className={`fixed left-0 right-0 z-50 transition-all duration-300 ${showTrustBar ? 'top-10' : 'top-0'} ${
          isScrolled || mobileMenuOpen
            ? "bg-[#FAF7F2] shadow-md py-3" 
            : "bg-[#FAF7F2]/95 backdrop-blur-sm py-5"
        }`}
        data-testid="main-header"
      >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between">
          <a href="#hero" className="flex items-center gap-3 shrink-0" data-testid="logo">
            <img 
              src="https://static.prod-images.emergentagent.com/jobs/e84fca2b-7e76-4834-b103-9bf7c18b7fcd/images/2d9573f2a9fc4e9c08165186a64d94d93f2109c8aeba2abfd768c1e7a458adbc.png" 
              alt="Mazal Logo" 
              className="h-12 md:h-14 w-auto mix-blend-multiply"
              width="56"
              height="56"
              decoding="async"
              loading="eager"
            />
            <div className="flex flex-col">
              <span className="font-['Heebo'] text-xl md:text-2xl font-bold text-[#2C2C2C] whitespace-nowrap">
                {language === 'he' ? 'מזל' : 'Mazal'}
              </span>
              <span className="text-xs text-[#C4956A] font-medium -mt-1 whitespace-nowrap">
                {language === 'he' ? 'קוסמטיקאית פרא-רפואית' : 'Paramedical Cosmetician'}
              </span>
            </div>
          </a>

          <nav className="hidden lg:flex items-center gap-6" aria-label={language === 'he' ? 'ניווט ראשי' : 'Main navigation'}>
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className={`transition-colors font-medium text-sm focus:outline-none focus:ring-2 focus:ring-[#C4956A] focus:ring-offset-2 rounded ${
                  link.isSpecial 
                    ? 'text-[#C4956A] hover:text-[#b38559] bg-[#C4956A]/10 px-3 py-1.5 rounded-full hover:bg-[#C4956A]/20' 
                    : 'text-[#2C2C2C] hover:text-[#C4956A]'
                }`}
              >
                {link.label}
              </a>
            ))}
            
            <LanguageSwitcher />
            
            <a href="#contact" onClick={(e) => handleNavClick(e, '#contact')}>
              <Button className="rounded-full px-6 py-2 bg-[#C4956A] hover:bg-[#b38559] hover:scale-[1.02] text-white transition-all focus:outline-none focus:ring-2 focus:ring-[#C4956A] focus:ring-offset-2" data-testid="header-cta">
                {t.nav.bookConsultation}
                <ArrowIcon className="w-4 h-4 mx-2" />
              </Button>
            </a>
          </nav>

          <div className="lg:hidden flex items-center gap-2">
            <LanguageSwitcher />
            <button
              className="p-2 text-[#2C2C2C] focus:outline-none focus:ring-2 focus:ring-[#C4956A] rounded"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
              aria-expanded={mobileMenuOpen}
              aria-label={mobileMenuOpen ? (language === 'he' ? 'סגור תפריט' : 'Close menu') : (language === 'he' ? 'פתח תפריט' : 'Open menu')}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.nav
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden pt-4 pb-4 bg-[#FAF7F2] border-t border-[#E5E0DA] mt-2"
              aria-label={language === 'he' ? 'תפריט נייד' : 'Mobile navigation'}
            >
              <div className="flex flex-col gap-3">
                {navLinks.map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    className={`transition-colors py-2 font-medium text-lg focus:outline-none focus:ring-2 focus:ring-[#C4956A] rounded ${
                      link.isSpecial 
                        ? 'text-[#C4956A] bg-[#C4956A]/10 px-4 rounded-lg' 
                        : 'text-[#2C2C2C] hover:text-[#C4956A]'
                    }`}
                    onClick={(e) => { handleNavClick(e, link.href); setMobileMenuOpen(false); }}
                  >
                    {link.label}
                  </a>
                ))}
                <a href="#contact" onClick={(e) => { handleNavClick(e, '#contact'); setMobileMenuOpen(false); }}>
                  <Button className="w-full rounded-full bg-[#C4956A] hover:bg-[#b38559] text-white mt-2 py-6">
                    {t.nav.bookConsultation}
                    <ArrowIcon className="w-4 h-4 mx-2" />
                  </Button>
                </a>
              </div>
            </motion.nav>
          )}
        </AnimatePresence>
      </div>
    </header>
    </>
  );
};
